import type { ChairSides } from '@peles/shared';
import type { CanvasItem } from '../types';
import { chairsOf, ftToPx, seatNumberOf, tableChairLayout, tableChairSides } from '../helpers';

/** Where one seat of a table is drawn on the canvas: its centre, in canvas px. */
export interface SeatSpot {
  seatNumber: number;
  x: number;
  y: number;
  /** Half the chair's size, for a marker that covers it. */
  r: number;
}

/**
 * Where a table's seats are, seat by seat. A table whose chairs were placed
 * answers with those chairs, where they stand — moved by hand or not — each
 * by the seat its label names (`S3` is seat 3). One with none placed answers
 * with where the canvas draws its chair preview, turned with the table.
 *
 * `occupied` are the seats somebody holds: one whose chair was taken away by
 * hand is still drawn — where the preview would put it — so whoever sits
 * there can be seen and moved.
 */
export function seatSpots(
  table: CanvasItem,
  items: CanvasItem[],
  opts: { chairSizeFt: number; chairSides: ChairSides; occupied?: number[] },
): SeatSpot[] {
  const placed = chairsOf(items, table);
  if (placed.length === 0) return previewSpots(table, opts, table.seats);
  const spots = placed.flatMap((chair) => {
    const seatNumber = seatNumberOf(chair);
    if (seatNumber == null) return [];
    const w = ftToPx(chair.widthFt);
    const h = ftToPx(chair.heightFt);
    return [{ seatNumber, x: chair.x + w / 2, y: chair.y + h / 2, r: Math.max(w, h) / 2 }];
  });
  const drawn = new Set(spots.map((spot) => spot.seatNumber));
  const missing = (opts.occupied ?? []).filter((seat) => !drawn.has(seat));
  if (missing.length === 0) return spots;
  const preview = previewSpots(table, opts, Math.max(table.seats, ...missing));
  return [...spots, ...preview.filter((spot) => missing.includes(spot.seatNumber))];
}

/** Where the canvas's chair preview puts `seats` chairs, turned with the table. */
function previewSpots(
  table: CanvasItem,
  opts: { chairSizeFt: number; chairSides: ChairSides },
  seats: number,
): SeatSpot[] {
  if (seats <= 0) return [];
  const { positions, centered } = tableChairLayout(table, {
    seats,
    chairSizeFt: opts.chairSizeFt,
    chairSides: tableChairSides(table, opts.chairSides),
  });
  const size = ftToPx(opts.chairSizeFt);
  const w = ftToPx(table.widthFt);
  const h = ftToPx(table.heightFt);
  const turn = (table.rotation * Math.PI) / 180;
  const cos = Math.cos(turn);
  const sin = Math.sin(turn);
  return positions.map((pos, i) => {
    // The chair's centre in the table's own frame, as the preview draws it.
    const lx = centered ? w / 2 + ftToPx(pos.x) : ftToPx(pos.x) + size / 2;
    const ly = centered ? h / 2 + ftToPx(pos.y) : ftToPx(pos.y) + size / 2;
    // Turned about the table's centre, as the canvas turns the table.
    const dx = lx - w / 2;
    const dy = ly - h / 2;
    return {
      seatNumber: i + 1,
      x: table.x + w / 2 + dx * cos - dy * sin,
      y: table.y + h / 2 + dx * sin + dy * cos,
      r: size / 2,
    };
  });
}

/** "Ann Cohen" → "AC"; one name → its first two letters. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
